/**
 * IndustryMatchPicker Component
 *
 * The popup shown after the customer has chosen a sector (and optionally a
 * subsector and industry group) in the three onboarding dropdowns. It asks
 * "Which of these matches your company the closest?" and lists 6-digit
 * NAICS 2022 industry titles under the deepest level chosen, 5 at a time
 * with "Show 5 more". When there are more than 5, the backend ranks them
 * by matching the customer's business description against the Census 2022
 * Index File (plain text, no AI). "None of these fit" returns the customer
 * to the dropdowns. The customer's pick is the code. No code numbers are
 * shown to the customer anywhere.
 *
 * v1.0 - 2026-10-28 - ONBOARDING-INDUSTRY-PICKER (Branko M31077): initial
 * implementation.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import api from '../services/api';

export interface IndustryMatch {
  code: string;
  title: string;
}

export interface IndustryMatchPickerProps {
  open: boolean;
  deepestCode: string;
  description: string;
  onPick: (match: IndustryMatch) => void;
  onNoneFit: () => void;
  onCancel: () => void;
}

const IndustryMatchPicker: React.FC<IndustryMatchPickerProps> = ({
  open,
  deepestCode,
  description,
  onPick,
  onNoneFit,
  onCancel,
}) => {
  const [matches, setMatches] = useState<IndustryMatch[]>([]);
  // TONIGHT-COMBINED CI-CORRECTION 4 (Branko 2026-10-28): the read of the
  // offset state value is unused — nextOffset is a function parameter the
  // caller passes in. react-scripts/CI treats @typescript-eslint/no-unused-
  // vars as an error, so drop the read destructure name. setOffset is still
  // used (fetchMatches advances it to nextOffset; reset to 0 on close).
  const [, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMatches = useCallback(
    async (nextOffset: number) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await api.post('/onboarding/industry-matches', {
          code: deepestCode,
          description,
          offset: nextOffset,
        });
        const body = response?.data || {};
        const incoming = Array.isArray(body.matches) ? body.matches : [];
        setMatches((prev) => (nextOffset === 0 ? incoming : prev.concat(incoming)));
        setHasMore(Boolean(body.has_more));
        setOffset(nextOffset);
      } catch (err: any) {
        setError(err?.message || 'Failed to load matches.');
      } finally {
        setIsLoading(false);
      }
    },
    [deepestCode, description]
  );

  useEffect(() => {
    if (open) {
      // Reset and fetch the first page every time the popup opens.
      setMatches([]);
      setOffset(0);
      setHasMore(false);
      fetchMatches(0);
    }
  }, [open, fetchMatches]);

  const handleShowMore = () => {
    if (matches.length === 0 && !isLoading) {
      fetchMatches(0);
      return;
    }
    fetchMatches(matches.length);
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Which of these matches your company the closest?</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <List dense disablePadding>
          {matches.map((m) => (
            <ListItem key={m.code} disablePadding>
              <ListItemButton onClick={() => onPick(m)}>
                <ListItemText primary={m.title} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!isLoading && matches.length === 0 && !error && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No matching industries were found.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 2 }}>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onNoneFit} color="inherit">
          None of these fit
        </Button>
        <Button
          onClick={handleShowMore}
          disabled={!hasMore || isLoading}
          variant="outlined"
        >
          Show 5 more
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IndustryMatchPicker;
